<script setup lang="ts">
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import type { ShipmentStopView } from '@/utils/shipment-detail-placeholder-data';
import { stopTypeBadgeClass } from '@/utils/shipment-detail-placeholder-data';
import { formatShipmentDateTime } from '@/utils/shipment-detail-format';
import { shipmentDetailCardPt } from '@/components/shipment/detail/shipment-detail-card-pt';

defineProps<{
  stops: ShipmentStopView[];
}>();

const cardPt = shipmentDetailCardPt;

function typeLabel(type: ShipmentStopView['type']): string {
  if (type === 'origin') return 'Origin';
  if (type === 'destination') return 'Destination';
  return 'Waypoint';
}

function statusSeverity(
  status: ShipmentStopView['status'],
): 'success' | 'info' | 'warn' | 'secondary' {
  if (status === 'delivered' || status === 'departed') return 'success';
  if (status === 'arrived') return 'info';
  return 'secondary';
}
</script>

<template>
  <div class="space-y-3">
    <Card v-for="(stop, index) in stops" :key="stop.id" :pt="cardPt">
      <template #content>
        <div class="flex gap-3">
          <div
            class="flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
            :class="
              stop.type === 'origin'
                ? 'bg-green-500'
                : stop.type === 'destination'
                  ? 'bg-slate-700'
                  : 'bg-blue-500'
            "
          >
            {{ index + 1 }}
          </div>
          <div class="min-w-0 flex-1">
            <div class="mb-2 flex flex-wrap items-center gap-2">
              <span
                class="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
                :class="stopTypeBadgeClass(stop.type)"
              >
                {{ typeLabel(stop.type) }}
              </span>
              <Tag
                :value="stop.status"
                :severity="statusSeverity(stop.status)"
                class="!text-[10px] !capitalize"
              />
            </div>
            <p class="text-sm font-medium text-neutral-900">
              {{ stop.address }}
            </p>
            <dl class="mt-2 space-y-1 text-xs">
              <div class="flex justify-between gap-2">
                <dt class="text-neutral-500">Planned</dt>
                <dd class="font-medium text-neutral-800">
                  {{ formatShipmentDateTime(stop.plannedAt) }}
                </dd>
              </div>
              <div class="flex justify-between gap-2">
                <dt class="text-neutral-500">Actual</dt>
                <dd class="font-medium text-neutral-800">
                  {{ formatShipmentDateTime(stop.actualAt) }}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>
